import { FileData } from '@applyboard/school-applications-types-lib'
import {
  RawApplicationPagePropsResponse,
  useGetApplicationPageProps,
} from './useGetApplicationPageProps'
import { LanguageProficiencySpecificField } from '@backend/students-api/routes/page-props/GET/types'
import { get } from 'lodash'

type GetLanguageProficiencyTypesProps = {
  files?: FileData
}

export type LanguageProficiencyTypeOption = { label: string } & LanguageProficiencySpecificField

export function useGetLanguageProficiencyTypes(props: GetLanguageProficiencyTypesProps) {
  const { applicationPageProps } = useGetApplicationPageProps()

  const uploadedFile = Object.entries(props.files || {})
    .map(([, fileInfo]) => fileInfo)
    .find(fileInfo => fileInfo?.activeRecord)

  const testTypeOptions: Record<string, LanguageProficiencyTypeOption> = {}

  function getSpecificField(fieldKey: string): LanguageProficiencySpecificField {
    const field = get(
      applicationPageProps,
      `specificFields.languageProficiency.proofTypes.${fieldKey}`,
    ) as LanguageProficiencySpecificField | undefined

    return {
      enabled: !!field?.enabled,
      required: !!field?.required,
      verifyExamEnabled: field?.verifyExamEnabled,
      timeout: field?.timeout,
      primaryIdFieldName: field?.primaryIdFieldName,
    }
  }

  if (isEnabled(applicationPageProps, 'ielts') || uploadedFile?.type === 'LANGUAGE_TEST_IELTS') {
    testTypeOptions.IELTS = { label: 'IELTS', ...getSpecificField('ielts') }
  }

  if (isEnabled(applicationPageProps, 'pte') || uploadedFile?.type === 'LANGUAGE_TEST_PTE') {
    testTypeOptions.PTE = { label: 'PTE Academic', ...getSpecificField('pte') }
  }

  if (isEnabled(applicationPageProps, 'toefl') || uploadedFile?.type === 'LANGUAGE_TEST_TOEFL') {
    testTypeOptions.TOEFL = { label: 'TOEFL®️', ...getSpecificField('toefl') }
  }

  if (
    isEnabled(applicationPageProps, 'duolingo') ||
    uploadedFile?.type === 'LANGUAGE_TEST_DUOLINGO'
  ) {
    testTypeOptions.DUOLINGO = { label: 'Duolingo', ...getSpecificField('duolingo') }
  }

  if (isEnabled(applicationPageProps, 'cael') || uploadedFile?.type === 'LANGUAGE_TEST_CAEL') {
    testTypeOptions.CAEL = { label: 'CAEL', ...getSpecificField('cael') }
  }

  if (
    isEnabled(applicationPageProps, 'cambridge') ||
    uploadedFile?.type === 'LANGUAGE_TEST_CAMBRIDGE'
  ) {
    testTypeOptions.CAMBRIDGE = { label: 'Cambridge', ...getSpecificField('cambridge') }
  }

  if (isEnabled(applicationPageProps, 'met') || uploadedFile?.type === 'LANGUAGE_TEST_MET') {
    testTypeOptions.MET = { label: 'MET', ...getSpecificField('met') }
  }

  if (isEnabled(applicationPageProps, 'epp') || uploadedFile?.type === 'LANGUAGE_TEST_EPP') {
    testTypeOptions.EPP = { label: 'English Pathway Program', ...getSpecificField('epp') }
  }

  if (isEnabled(applicationPageProps, 'other') || uploadedFile?.type === 'LANGUAGE_TEST_OTHER') {
    testTypeOptions.OTHER = { label: 'Other', ...getSpecificField('other') }
  }

  if (
    isEnabled(applicationPageProps, 'canTest') ||
    uploadedFile?.type === 'LANGUAGE_TEST_CANTEST'
  ) {
    testTypeOptions.CANTEST = { label: 'CanTEST', ...getSpecificField('canTest') }
  }

  if (
    isEnabled(applicationPageProps, 'languageCertEsol') ||
    uploadedFile?.type === 'LANGUAGE_TEST_LANGUAGECERT_ESOL'
  ) {
    testTypeOptions.LANGUAGECERT_ESOL = {
      label: 'LanguageCert International ESOL',
      ...getSpecificField('languageCertEsol'),
    }
  }

  if (
    isEnabled(applicationPageProps, 'leap4Advanced') ||
    uploadedFile?.type === 'LANGUAGE_TEST_LEAP_4_ADVANCED'
  ) {
    testTypeOptions.LEAP_4_ADVANCED = {
      label: 'LEAP 4 Advanced',
      ...getSpecificField('leap4Advanced'),
    }
  }

  return testTypeOptions
}

function isEnabled(props: RawApplicationPagePropsResponse['data'] | undefined, field: string) {
  return get(props, `specificFields.languageProficiency.proofTypes.${field}.enabled`)
}
